import {
	createPharmaDoctorAPI,
	masterPharmaDoctorsAPI,
} from "../../../PharmaDoctors/PharmaDoctorService";
import {
	buildDoctorApiPayload,
	buildDoctorSelectOption,
	extractCreatedDoctorId,
	mapMasterDoctorToForm,
	mapPrescriptionDoctorToForm,
	pickBestDoctorMatch,
	isPlaceholderDoctorName,
	sanitizeDoctorName,
} from "./doctorFormUtils";

export const searchMasterDoctors = async (search) => {
	const term = String(search || "").trim();
	if (term.length < 2) return [];

	try {
		const response = await masterPharmaDoctorsAPI({ search: term });
		return response?.data?.data || [];
	} catch (error) {
		console.error("Error searching master doctors:", error);
		return [];
	}
};

/**
 * When a prescription includes doctor details, search master first.
 * If matched, link doctorId; otherwise prefill the form only.
 */
export const resolveDoctorFromPrescription = async (prescriptionDoctor = {}) => {
	const doctorForm = mapPrescriptionDoctorToForm(prescriptionDoctor);
	if (!doctorForm.name.trim() || isPlaceholderDoctorName(doctorForm?.name)) {
		return {
			doctorId: null,
			doctorForm,
			selectedDoctorOption: null,
		};
	}

	const doctors = await searchMasterDoctors(doctorForm.name);
	const match = pickBestDoctorMatch(doctors, {
		...prescriptionDoctor,
		...doctorForm,
	});

	if (match?._id) {
		const linkedForm = mapMasterDoctorToForm(match);
		return {
			doctorId: match._id,
			doctorForm: linkedForm,
			selectedDoctorOption: buildDoctorSelectOption(match),
		};
	}

	return {
		doctorId: null,
		doctorForm,
		selectedDoctorOption: null,
	};
};

/**
 * Resolve doctorId before saving a sale.
 * Uses existing id, else searches master by name, else creates a new doctor.
 */
export const resolveDoctorIdForSale = async ({ doctorId, doctorForm }) => {
	if (doctorId) return doctorId;

	const name = sanitizeDoctorName(doctorForm?.name);
	if (!name) return null;

	const doctors = await searchMasterDoctors(name);
	const match = pickBestDoctorMatch(doctors, { ...doctorForm, name });
	if (match?._id) return match._id;

	const payload = buildDoctorApiPayload(doctorForm);
	const response = await createPharmaDoctorAPI(payload);
	const newDoctorId = extractCreatedDoctorId(response);

	if (!newDoctorId) {
		throw new Error("Doctor created but ID was not returned");
	}

	return newDoctorId;
};
